import React, { memo, useState } from "react";
import { toast } from "react-toastify";
import { OTAComponentCreatePayload } from "../../types";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  ModalFooter,
  CancelButton,
  SubmitButton,
} from "./styles";

interface AddComponentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: OTAComponentCreatePayload) => Promise<void>;
  componentTypes: string[];
}

export const AddComponentModal = memo<AddComponentModalProps>(
  ({ isOpen, onClose, onSubmit, componentTypes }) => {
    const [name, setName] = useState("");
    const [type, setType] = useState("");
    const [description, setDescription] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const resetForm = () => {
      setName("");
      setType("");
      setDescription("");
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (!name.trim()) {
        toast.error("Component name is required");
        return;
      }

      if (!type) {
        toast.error("Component type is required");
        return;
      }

      setIsSubmitting(true);
      try {
        const payload: OTAComponentCreatePayload = {
          name: name.trim(),
          type: type.trim() || undefined,
          description: description.trim() || undefined,
        };

        await onSubmit(payload);
        resetForm();
        onClose();
      } catch (err: unknown) {
        const error = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "Failed to save component";
        toast.error(errorMsg);
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleClose = () => {
      if (!isSubmitting) {
        resetForm();
        onClose();
      }
    };

    if (!isOpen) return null;

    return (
      <ModalOverlay onClick={handleClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Add New Component</ModalTitle>
            <CloseButton onClick={handleClose} disabled={isSubmitting}>
              ×
            </CloseButton>
          </ModalHeader>

          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormGroup>
                <Label htmlFor="name">
                  Component Name <span style={{ color: "#E74C3C" }}>*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Autonomy Stack, ECU Firmware"
                  required
                  disabled={isSubmitting}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="type">
                  Type <span style={{ color: "#E74C3C" }}>*</span>
                </Label>
                <Select
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">— Select a type —</option>
                  {componentTypes.map((t) => (
                    <option key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </option>
                  ))}
                </Select>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#606060",
                    marginTop: "4px",
                  }}
                >
                  AWS Component Name will be auto-generated as:
                  com.cyngn.&lt;name&gt;
                </div>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="description">Description</Label>
                <TextArea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the component's purpose and functionality..."
                  rows={4}
                  disabled={isSubmitting}
                />
              </FormGroup>
            </ModalBody>

            <ModalFooter>
              <CancelButton
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </CancelButton>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Create Component"}
              </SubmitButton>
            </ModalFooter>
          </form>
        </ModalContainer>
      </ModalOverlay>
    );
  }
);
